import styled from 'styled-components'

export const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--background);
`

export const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2.5rem 2.5rem 2rem;

  @media screen and (max-width: 768px) {
    padding: 1.5rem 1.25rem 1.5rem;
  }
`

export const CALENDAR_ICON_URL = '/icon/calendar.svg'

export const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 1.25rem;
  line-height: 1.3;
`

export const InfoBox = styled.aside`
  background-color: var(--white);
  border: 1px solid var(--border);
  border-left: 4px solid var(--primary);
  border-radius: 0.375rem;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.25rem;
  box-shadow:
    0px var(--Elevation1OffsetY1, 1px) var(--Elevation1Radius1, 2px) 0px var(--shadow-smooth),
    0px var(--Elevation1OffsetY2, 2px) var(--Elevation1Radius2, 4px) 0px var(--shadow-smooth);
`

export const InfoList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.6;

  li + li {
    margin-top: 0.375rem;
  }
`

export const AvailabilityButton = styled.button<{ $isActive?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid ${({ $isActive }) => ($isActive ? 'var(--primary)' : 'var(--border)')};
  border-radius: 0.375rem;
  background-color: var(--white);
  color: ${({ $isActive }) => ($isActive ? 'var(--primary)' : 'var(--grey-lighter)')};
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1rem;
  cursor: ${({ $isActive }) => ($isActive ? 'pointer' : 'not-allowed')};
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0.85)};
  margin-bottom: 2rem;
  width: fit-content;
  box-shadow: none;

  &:hover {
    background-color: var(--white);
    color: ${({ $isActive }) => ($isActive ? 'var(--primary)' : 'var(--grey-lighter)')};
    border-color: ${({ $isActive }) => ($isActive ? 'var(--primary)' : 'var(--border)')};
  }

  &:focus,
  &:active {
    outline: none;
    box-shadow: none;
  }
`

export const AvailabilityButtonIcon = styled.span<{ $isActive?: boolean }>`
  width: 18px;
  height: 18px;
  display: block;
  flex-shrink: 0;
  background-color: ${({ $isActive }) =>
    $isActive ? 'var(--primary)' : 'var(--grey-lighter)'};
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0.45)};
  mask: url(${CALENDAR_ICON_URL}) no-repeat center / contain;
  -webkit-mask: url(${CALENDAR_ICON_URL}) no-repeat center / contain;
`

export const SpaceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2.5rem;

  @media screen and (min-width: 992px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`

export const EmptyStateSection = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem 1rem 3rem;
  min-height: 320px;
`

export const EmptyStateMessage = styled.p`
  font-size: 1rem;
  font-weight: 400;
  color: var(--grey-lighter);
  text-align: center;
  margin: 0;
  max-width: 420px;
  line-height: 1.5;
`

export const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
  padding: 0.625rem 1.25rem;
  border: 2px solid var(--primary);
  border-radius: 4px;
  background-color: var(--white);
  color: var(--primary);
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1rem;
  cursor: pointer;

  &:hover {
    background-color: var(--primary);
    color: var(--text-over-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--input-focus-shadow);
    outline-offset: 2px;
  }
`

export const CancelBanner = styled.aside`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  max-width: 1200px;
  min-height: 72px;
  padding: 0 24px;
  margin-top: auto;
  background-color: var(--white);
  border: 1px solid var(--border);
  border-left: 5px solid var(--red);
  border-radius: 4px;
  opacity: 1;
  box-sizing: border-box;
  box-shadow:
    0px var(--Elevation1OffsetY1, 1px) var(--Elevation1Radius1, 2px) 0px var(--shadow-smooth),
    0px var(--Elevation1OffsetY2, 2px) var(--Elevation1Radius2, 4px) 0px var(--shadow-smooth);

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    min-height: auto;
    padding: 16px;
    gap: 12px;
  }
`

export const CancelBannerContent = styled.div`
  flex: 1;
  min-width: 0;
  padding: 12px 0;

  @media screen and (max-width: 768px) {
    padding: 0;
    width: 100%;
  }
`

export const CancelBannerTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--red);
  margin: 0 0 4px;
  line-height: 1.3;

  @media screen and (max-width: 768px) {
    font-size: 1rem;
  }
`

export const CancelBannerDescription = styled.p`
  font-size: 0.875rem;
  font-weight: 400;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.5;
  word-break: break-word;
  overflow-wrap: anywhere;

  @media screen and (max-width: 768px) {
    font-size: 0.8125rem;
    line-height: 1.45;
  }
`

export const CancelReservationButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0.625rem 1.25rem;
  border: none;
  border-radius: 4px;
  background-color: var(--red);
  color: var(--white);
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1rem;
  cursor: pointer;
  white-space: nowrap;
  opacity: 1;

  &:hover {
    background-color: var(--red);
  }

  &:focus,
  &:active {
    outline: none;
    box-shadow: none;
  }

  @media screen and (max-width: 768px) {
    width: 100%;
  }
`

export const NavigationFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  padding: 1.25rem 2.5rem;
  background-color: var(--white);
  border-top: 1px solid var(--border);
  position: sticky;
  bottom: 0;
  z-index: 10;

  @media screen and (max-width: 768px) {
    padding: 1rem 1.25rem;
    flex-direction: row;
    flex-wrap: wrap;
  }
`

export const NavButtonBack = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem 1.5rem;
  border: 2px solid var(--primary);
  border-radius: 4px;
  background-color: var(--white);
  color: var(--primary);
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1rem;
  cursor: pointer;
  white-space: nowrap;
  min-width: 120px;

  &:hover {
    background-color: var(--primary);
    color: var(--white);
  }

  &:focus,
  &:active {
    outline: none;
    box-shadow: none;
  }

  @media screen and (max-width: 480px) {
    flex: 1;
  }
`

export const NavButtonPrimary = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem 1.5rem;
  border: none;
  border-radius: 4px;
  background-color: var(--primary);
  color: var(--white);
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1rem;
  cursor: pointer;
  white-space: nowrap;
  min-width: 160px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: var(--primary);
    opacity: 0.9;
  }

  &:focus,
  &:active {
    outline: none;
    box-shadow: none;
  }

  @media screen and (max-width: 480px) {
    flex: 1;
  }
`
